import React, { useState, useEffect, useCallback } from "react";
import {
  Bell,
  CheckCircle,
  AlertCircle,
  Calendar,
  MessageSquare,
  Star,
} from "lucide-react";
import { cn, formatDateTime } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useAppStore } from "@/store/AppStore";
import { toast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import useNotifications from "@/hooks/useNotification";
import {
  useNotifications as useNotificationsQuery,
} from "@/hooks/useNotifications";

type TabType = "all" | "unread" | "read";

export const NotificationsPage: React.FC = () => {
  const { user, token } = useAuth();
  const { state, setUnreadCount, decrementUnreadCount, resetUnreadCount } =
    useAppStore();
  const [activeTab, setActiveTab] = useState<TabType>("all");
  // const [selectedNotification, setSelectedNotification] = useState<
  //   string | null
  // >(null);
  const [hasRequestedPermission, setHasRequestedPermission] = useState(false);

  // API params based on active tab (backend expects strings and offset instead of page)
  const apiParams = {
    type: undefined, // Filter by notification type if needed
    search: undefined, // Search term if needed
    limit: "50",
    offset: "0",
  };

  // Get auth token for WebSocket connection
  const authToken = token || "";

  // Initialize WebSocket connection for real-time notifications
  const { socket, unreadCount: realtimeUnreadCount } =
    useNotifications(authToken);

  // Fetch notifications using React Query
  const {
    data: notificationData,
    isLoading,
    error,
    refetch,
  } = useNotificationsQuery(user?.id, apiParams);


  const allNotifications = notificationData || [];

  // Filter notifications based on active tab (client-side filtering since backend doesn't support isRead filter)
  const filteredNotifications = allNotifications.filter((notification) => {
    switch (activeTab) {
      case "unread":
        return !notification.isRead;
      case "read":
        return notification.isRead;
      default:
        return true;
    }
  });

  const unreadCount = state.unreadCount || 0;

  // Request browser notification permission
  useEffect(() => {
    if (
      !hasRequestedPermission &&
      "Notification" in window &&
      Notification.permission === "default"
    ) {
      setHasRequestedPermission(true);
      Notification.requestPermission();
    }
  }, [hasRequestedPermission]);

  // Update context store unread count from socket
  useEffect(() => {
    if (realtimeUnreadCount !== undefined) {
      setUnreadCount(realtimeUnreadCount);
    }
  }, [realtimeUnreadCount, setUnreadCount]);

  // Listen for mark as read success events
  useEffect(() => {
    if (socket) {
      // Handle successful mark as read
      const handleMarkReadSuccess = (data: { notificationId: string }) => {
        console.log("Notification marked as read:", data.notificationId);

        // Invalidate and refetch notification queries
        queryClient.invalidateQueries({
          queryKey: ["notifications"],
          exact: false,
        });

        // Note: Removed toast to prevent duplicates - optimistic updates provide sufficient feedback
      };

      // Handle successful mark all as read
      const handleMarkAllReadSuccess = () => {
        console.log("All notifications marked as read");

        // Invalidate and refetch notification queries
        queryClient.invalidateQueries({
          queryKey: ["notifications"],
          exact: false,
        });

        // Reset context store unread count
        resetUnreadCount();

        // Note: Removed toast to prevent duplicates - optimistic updates provide sufficient feedback
      };

      socket.on("mark_read_success", handleMarkReadSuccess);
      socket.on("mark_all_read_success", handleMarkAllReadSuccess);

      return () => {
        socket.off("mark_read_success", handleMarkReadSuccess);
        socket.off("mark_all_read_success", handleMarkAllReadSuccess);
      };
    }
  }, [socket, resetUnreadCount]);

  // All useCallback hooks must be called before any early returns
  const handleMarkAsRead = useCallback(
    (notificationId: string) => {
      console.log("handle mark read");
      if (!socket) return;

      // Emit socket event to mark as read
      socket.emit("mark_read", { notificationId });

      // Optimistically update the unread count
      if (state.unreadCount > 0) {
        decrementUnreadCount();
      }
    },
    [socket, state.unreadCount, decrementUnreadCount]
  );




  const handleMarkAllAsRead = useCallback(async () => {
    try {
      if (!user || !socket) return;
      const unreadNotifications = allNotifications?.filter(
        (n: any) => !n.isRead
      );
      if (unreadNotifications.length === 0) return;

      // Emit socket event to mark all as read
      socket.emit("mark_all_read", { userId: user.id });

      // Optimistically update the UI
      resetUnreadCount();

      // Optimistically invalidate queries to trigger refetch
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      }, 100);
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast({
        title: "Error",
        description:
          "Failed to mark all notifications as read. Please try again.",
        variant: "destructive",
      });
    }
  }, [user, socket, allNotifications, resetUnreadCount]);

  const getNotificationIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "session":
        return Calendar;
      case "message":
        return MessageSquare;
      case "review":
        return Star;
      case "success":
        return CheckCircle;
      case "warning":
        return AlertCircle;
      case "error":
        return AlertCircle;
      case "info":
        return Bell;
      default:
        return Bell;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "session":
        return "text-blue-600 bg-blue-100";
      case "message":
        return "text-purple-600 bg-purple-100";
      case "review":
        return "text-yellow-600 bg-yellow-100";
      case "success":
        return "text-green-600 bg-green-100";
      case "warning":
        return "text-orange-600 bg-orange-100";
      case "error":
        return "text-red-600 bg-red-100";
      case "info":
        return "text-gray-600 bg-gray-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error loading notifications
            </h3>
            <p className="text-gray-600 mb-4">
              There was an error loading your notifications.
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600 mt-1">
            Stay updated with your student interactions
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors mt-4 sm:mt-0"
          >
            Mark All as Read
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {(
              [
                { key: "all", label: "All", count: allNotifications.length },
                { key: "unread", label: "Unread", count: unreadCount },
                {
                  key: "read",
                  label: "Read",
                  count: allNotifications.length - unreadCount,
                },
              ] as const
            ).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  "py-4 px-1 border-b-2 font-medium text-sm transition-colors",
                  activeTab === tab.key
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                )}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={cn(
                      "ml-2 px-2 py-1 text-xs rounded-full",
                      activeTab === tab.key
                        ? "bg-primary text-white"
                        : "bg-gray-100 text-gray-600"
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Notifications List */}
        <div className="divide-y divide-gray-200">
          {filteredNotifications.map((notification) => {
            const NotificationIcon = getNotificationIcon(notification.type);
            return (
              <div
                key={notification.id}
                className={cn(
                  "p-6 hover:bg-gray-50 transition-colors",
                  !notification.isRead && "bg-blue-50/30"
                )}
              >
                <div className="flex items-start space-x-4">
                  {/* Icon */}
                  <div
                    className={cn(
                      "flex-shrink-0 p-2 rounded-lg",
                      getNotificationColor(notification.type)
                    )}
                  >
                    <NotificationIcon className="h-5 w-5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3
                            className={cn(
                              "text-sm font-medium",
                              notification.isRead
                                ? "text-gray-900"
                                : "text-gray-900 font-semibold"
                            )}
                          >
                            {notification.title}
                          </h3>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-primary rounded-full"></div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>{formatDateTime(notification.createdAt)}</span>
                          {notification.metadata?.studentName && (
                            <span>
                              Student: {notification.metadata.studentName}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-2 ml-4">
                        {!notification.isRead && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Mark as read"
                          >
                            <CheckCircle className="h-6 w-6" />
                          </button>
                        )}

                        {/* More actions */}
                        {/* <div className="relative">
                          <button
                            onClick={() => setSelectedNotification(
                              selectedNotification === notification.id ? null : notification.id
                            )}
                            className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>

                          {selectedNotification === notification.id && (
                            <>
                              <div 
                                className="fixed inset-0 z-10"
                                onClick={() => setSelectedNotification(null)}
                              />
                              <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                                <div className="py-1">
                                  <button className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2">
                                    <Eye className="h-4 w-4" />
                                    <span>View Details</span>
                                  </button>
                                  <button
                                    onClick={() => handleArchive(notification.id)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2"
                                  >
                                    <Archive className="h-4 w-4" />
                                    <span>Archive</span>
                                  </button>
                                  <button
                                    onClick={() => handleDelete(notification.id)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 text-red-600 flex items-center space-x-2"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    <span>Delete</span>
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {filteredNotifications.length === 0 && (
          <div className="p-12 text-center">
            <Bell className="h-8 w-8 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {activeTab === "unread"
                ? "No unread notifications"
                : activeTab === "read"
                ? "No read notifications"
                : "No notifications"}
            </h3>
            <p className="text-gray-600">
              {activeTab === "unread"
                ? "All caught up! No new notifications to review."
                : activeTab === "read"
                ? "No notifications have been marked as read yet."
                : "You'll see important updates and messages here."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
